import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  PageHeader, ListToolbar, DataTable, Badge, Chip, HealthBadge, useViewMode,
} from '@/components/ui'
import type { Column, BadgeTone } from '@/components/ui'
import { Sparkline } from '@/components/charts'
import { C } from '@/lib/colors'
import { useStore } from '@/store/useStore'
import { accountById, fundById, latest } from '@/lib/selectors'
import { money, num, pct } from '@/lib/format'
import type { Property } from '@/types/entities'
import './PropertiesList.css'

type ViewId = 'all' | 'owned' | 'managed' | 'archived'

const VIEWS = [
  { id: 'all', label: 'All' },
  { id: 'owned', label: 'Owned' },
  { id: 'managed', label: 'Managed' },
  { id: 'archived', label: 'Archived' },
]

const REL_TONE: Record<string, BadgeTone> = {
  Owned: 'navy', 'Managed-3PM': 'info', JV: 'navy', Development: 'warn',
}
const REL_LABEL: Record<string, string> = {
  Owned: 'Owned', 'Managed-3PM': '3PM', JV: 'JV', Development: 'Dev',
}

export default function PropertiesList() {
  const data = useStore((s) => s.data)
  const navigate = useNavigate()
  const [view, setView] = useState<ViewId>('all')
  const [search, setSearch] = useState('')
  const [accountFilter, setAccountFilter] = useState<string | null>(null)
  const [viewMode, setViewMode] = useViewMode('properties', 'rows')

  // Accounts that actually own/manage properties, for the chip row.
  const accountChips = useMemo(() => {
    const counts = new Map<string, number>()
    for (const p of data.properties) {
      if (!p.ownerAccountId) continue
      if (p.archived || accountById(data, p.ownerAccountId)?.archived) continue
      counts.set(p.ownerAccountId, (counts.get(p.ownerAccountId) ?? 0) + 1)
    }
    return [...counts.entries()]
      .map(([id, count]) => ({ id, name: accountById(data, id)?.name ?? id, count }))
      .sort((a, b) => b.count - a.count)
  }, [data])

  // The chip row stays readable no matter how many title-holders the live
  // data carries: top accounts get chips, the long tail folds into a select.
  const CHIP_LIMIT = 10
  const topChips = accountChips.slice(0, CHIP_LIMIT)
  const overflowChips = accountChips.slice(CHIP_LIMIT)
  const activeOverflowChip = overflowChips.find((a) => a.id === accountFilter)

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase()
    return data.properties.filter((p) => {
      // Archived = the property itself, or its owner account, was archived.
      const owner = p.ownerAccountId ? accountById(data, p.ownerAccountId) : undefined
      const isArchived = !!(p.archived || owner?.archived)
      if (view === 'archived' && !isArchived) return false
      if (view !== 'archived' && isArchived) return false
      if (view === 'owned' && p.relationshipType !== 'Owned') return false
      if (view === 'managed' && p.relationshipType !== 'Managed-3PM') return false
      if (accountFilter && p.ownerAccountId !== accountFilter) return false
      if (q) {
        const acct = accountById(data, p.ownerAccountId)?.name ?? ''
        const hay = [
          p.name, p.externalIds?.lcode ?? '', p.address?.city ?? '',
          p.address?.state ?? '', acct,
        ].join(' ').toLowerCase()
        if (!hay.includes(q)) return false
      }
      return true
    })
  }, [data, view, search, accountFilter])

  const columns: Column<Property>[] = useMemo(() => [
    {
      key: 'name',
      header: 'Property',
      sortValue: (p) => p.name.toLowerCase(),
      render: (p) => (
        <div className="prop-name-cell">
          <Link to={`/properties/${p.id}`} className="prop-name" onClick={(e) => e.stopPropagation()}>
            {p.name}
          </Link>
          {p.brand && p.brand !== 'US Storage Centers' && (
            <span className="prop-brand dim">{p.brand}</span>
          )}
        </div>
      ),
    },
    {
      key: 'lcode',
      header: 'Lcode',
      width: 78,
      sortValue: (p) => p.externalIds?.lcode ?? '',
      render: (p) => <span className="prop-lcode mono-tnum">{p.externalIds?.lcode ?? '—'}</span>,
    },
    {
      key: 'account',
      header: 'Account',
      sortValue: (p) => accountById(data, p.ownerAccountId)?.name?.toLowerCase() ?? '',
      render: (p) => {
        const acct = accountById(data, p.ownerAccountId)
        if (!acct) return <span className="dim">—</span>
        return (
          <Link to={`/accounts/${acct.id}`} className="prop-acct" onClick={(e) => e.stopPropagation()}>
            {acct.name}
          </Link>
        )
      },
    },
    {
      key: 'location',
      header: 'Location',
      sortValue: (p) => `${p.address?.state ?? ''} ${p.address?.city ?? ''}`,
      render: (p) =>
        p.address?.city
          ? <span className="prop-loc">{p.address.city}{p.address.state ? <span className="dim">, {p.address.state}</span> : null}</span>
          : <span className="dim">—</span>,
    },
    {
      key: 'rel',
      header: 'Type',
      width: 92,
      align: 'center',
      sortValue: (p) => p.relationshipType,
      render: (p) => (
        <Badge tone={REL_TONE[p.relationshipType] ?? 'neutral'} variant="soft">
          {REL_LABEL[p.relationshipType] ?? p.relationshipType}
        </Badge>
      ),
    },
    {
      key: 'nrsf',
      header: 'NRSF',
      align: 'right',
      width: 92,
      sortValue: (p) => p.nrsf ?? 0,
      render: (p) => <span className="num">{num(p.nrsf)}</span>,
    },
    {
      key: 'units',
      header: 'Units',
      align: 'right',
      width: 76,
      sortValue: (p) => p.totalUnits ?? 0,
      render: (p) => <span className="num">{num(p.totalUnits)}</span>,
    },
    {
      key: 'occ',
      header: 'Occupancy',
      align: 'right',
      width: 168,
      sortValue: (p) => latest(p.occupancyPct) ?? -1,
      render: (p) => {
        const occ = latest(p.occupancyPct)
        const series = (p.occupancyPct ?? []).map((s) => s.value)
        return (
          <div className="prop-occ-cell">
            {series.length > 2 && (
              <Sparkline data={series} width={64} height={20} color={C.blue60} fill={false} strokeWidth={1.5} />
            )}
            <span className="num prop-occ-val">{pct(occ)}</span>
          </div>
        )
      },
    },
    {
      key: 'revenue',
      header: 'In-place Rev / mo',
      align: 'right',
      width: 124,
      sortValue: (p) => latest(p.revenue) ?? -1,
      render: (p) => <span className="num">{money(latest(p.revenue), { compact: true })}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      width: 110,
      align: 'center',
      sortValue: (p) => p.stabilization ?? p.state,
      render: (p) =>
        p.stabilization === 'Lease-Up'
          ? <Badge tone="warn" variant="soft" dot>Lease-Up</Badge>
          : p.stabilization === 'Stabilized'
            ? <Badge tone="ok" variant="soft" dot>Stabilized</Badge>
            : <Badge tone="neutral" variant="soft">{p.state}</Badge>,
    },
    {
      key: 'fund',
      header: 'Fund',
      sortValue: (p) => fundById(data, p.fundId)?.name?.toLowerCase() ?? 'zzz',
      render: (p) => {
        const fund = fundById(data, p.fundId)
        if (!fund) return <span className="dim">—</span>
        return (
          <Link to={`/funds/${fund.id}`} className="prop-fund" onClick={(e) => e.stopPropagation()}>
            {fund.name}
          </Link>
        )
      },
    },
  ], [data])

  return (
    <>
      <PageHeader
        eyebrow="Portfolio"
        title={<>All <b>Properties</b></>}
        sub="The full self-storage portfolio across owned and third-party-managed assets."
      />

      <ListToolbar
        views={VIEWS}
        activeView={view}
        onView={(id) => setView(id as ViewId)}
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Search by name, Lcode, city…"
        count={rows.length}
        viewMode={viewMode}
        onViewMode={setViewMode}
        onExport={() => { /* export stub */ }}
        extra={
          <div className="prop-chiprow">
            <Chip active={accountFilter === null} onClick={() => setAccountFilter(null)}>
              All accounts
            </Chip>
            {topChips.map((a) => (
              <Chip
                key={a.id}
                active={accountFilter === a.id}
                onClick={() => setAccountFilter((cur) => (cur === a.id ? null : a.id))}
              >
                {a.name} <span className="prop-chip-count">{a.count}</span>
              </Chip>
            ))}
            {/* Active overflow selection surfaces as a removable chip. */}
            {activeOverflowChip && (
              <Chip active onClick={() => setAccountFilter(null)}>
                {activeOverflowChip.name} <span className="prop-chip-count">{activeOverflowChip.count}</span>
              </Chip>
            )}
            {overflowChips.length > 0 && (
              <select
                className="prop-chip-more"
                value={activeOverflowChip ? accountFilter ?? '' : ''}
                onChange={(e) => setAccountFilter(e.target.value || null)}
                aria-label="More accounts"
              >
                <option value="">{`+${overflowChips.length} more accounts…`}</option>
                {overflowChips.map((a) => (
                  <option key={a.id} value={a.id}>{`${a.name} (${a.count})`}</option>
                ))}
              </select>
            )}
          </div>
        }
      />

      {viewMode === 'cards' ? (
        rows.length === 0 ? (
          <div className="prop-table-card card">
            <div className="prop-cards-empty dim">No properties match this filter.</div>
          </div>
        ) : (
          <div className="list-card-grid">
            {rows.map((p) => (
              <PropertyCard key={p.id} property={p} navigate={navigate} />
            ))}
          </div>
        )
      ) : (
        <div className="prop-table-card card">
          <DataTable
            columns={columns}
            rows={rows}
            getRowKey={(p) => p.id}
            onRowClick={(p) => navigate(`/properties/${p.id}`)}
            initialSort={{ key: 'nrsf', dir: 'desc' }}
            dense
            emptyText="No properties match this filter."
          />
        </div>
      )}
    </>
  )

  function PropertyCard({ property: p, navigate: go }: { property: Property; navigate: (to: string) => void }) {
    const acct = accountById(data, p.ownerAccountId)
    const occ = latest(p.occupancyPct)
    const occSeries = (p.occupancyPct ?? []).map((s) => s.value)
    const rev = latest(p.revenue)
    const city = p.address?.city
    const state = p.address?.state

    return (
      <div
        className="card card--clickable prop-card"
        role="link"
        tabIndex={0}
        onClick={() => go(`/properties/${p.id}`)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(`/properties/${p.id}`) }
        }}
      >
        <div className="prop-card-top">
          <div className="prop-card-titles">
            <Link to={`/properties/${p.id}`} className="prop-name" onClick={(e) => e.stopPropagation()}>
              {p.name}
            </Link>
            <div className="prop-card-sub">
              <span className="prop-lcode mono-tnum">{p.externalIds?.lcode ?? '—'}</span>
              {city && (
                <span className="prop-loc">
                  {city}{state ? <span className="dim">, {state}</span> : null}
                </span>
              )}
            </div>
          </div>
          <Badge tone={REL_TONE[p.relationshipType] ?? 'neutral'} variant="soft">
            {REL_LABEL[p.relationshipType] ?? p.relationshipType}
          </Badge>
        </div>

        <div className="prop-card-acct">
          {acct ? (
            <Link to={`/accounts/${acct.id}`} className="prop-acct" onClick={(e) => e.stopPropagation()}>
              {acct.name}
            </Link>
          ) : (
            <span className="dim">Unassigned</span>
          )}
        </div>

        <div className="prop-card-metrics">
          <div className="prop-card-metric">
            <span className="prop-card-metric-label">Occupancy</span>
            <div className="prop-card-metric-occ">
              {occSeries.length > 2 && (
                <Sparkline data={occSeries} width={56} height={18} color={C.blue60} fill={false} strokeWidth={1.5} />
              )}
              <span className="num prop-card-metric-val">{pct(occ)}</span>
            </div>
          </div>
          <div className="prop-card-metric prop-card-metric--right">
            <span className="prop-card-metric-label">In-place Rev / mo</span>
            <span className="num prop-card-metric-val">{money(rev, { compact: true })}</span>
          </div>
        </div>

        <div className="prop-card-foot">
          {p.stabilization === 'Lease-Up'
            ? <Badge tone="warn" variant="soft" dot>Lease-Up</Badge>
            : p.stabilization === 'Stabilized'
              ? <Badge tone="ok" variant="soft" dot>Stabilized</Badge>
              : <Badge tone="neutral" variant="soft">{p.state}</Badge>}
          {p.health && <HealthBadge health={p.health} />}
        </div>
      </div>
    )
  }
}
